import { useContext } from 'react'

import {
  Button, Form, LoadingModule, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useOrderItem from '@hooks/useOrderItem'
import useOrderItemTasks from '@hooks/useOrderItemTasks'
import useOrderItemTaskTypes from '@hooks/useOrderItemTaskTypes'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import NewTask from './components/NewTask'
import Task from './components/Task'

const MODAL_KEY = 'ManageOrderItemTasksModal'

type ModalCallbacks = {
  closeModal: () => void,
  createOrderItemTask: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageOrderItemTasksModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { allowedTaskTypes, orderItem: initOrderItem } = modalPayload

  const {
    canCreateTask,
    orderItem,
  } = useOrderItem(initOrderItem)

  const orderItemTasksPayload = useOrderItemTasks({
    filters: { order_item_id: orderItem.id },
    performHttpRequests: !!initOrderItem.id,
    requestOptions: {
      include_task_attachment_image: true,
      include_task_attachments: true,
      include_task_instruction_image: true,
      include_task_type: true,
    },
  })

  const {
    filteredOrderItemTasks,
    hasFilteredOrderItemTasks,
    loading: loadingTasks,
  } = orderItemTasksPayload

  const orderItemTaskTypesPayload = useOrderItemTaskTypes({
    filters: { allowed_task_types: allowedTaskTypes },
    performHttpRequests: !!initOrderItem.id,
  })

  const {
    filteredOrderItemTaskTypes,
    hasFilteredOrderItemTaskTypes,
    loading: loadingTaskTypes,
  } = orderItemTaskTypesPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Item" titleSecondLine="Tasks" />

      <SidebarModal.Content>
        {/*
         // @ts-expect-error TS2322 */}
        <LoadingModule loading={loadingTaskTypes} />

        {!loadingTaskTypes && (
          <>
            {canCreateTask && hasFilteredOrderItemTaskTypes && (
              <Form.Field direction="column" label="Order a New Task">
                {filteredOrderItemTaskTypes.map(taskType => (
                  <NewTask key={taskType.id} taskType={taskType} orderItem={orderItem} />
                ))}
              </Form.Field>
            )}

            {!loadingTasks && hasFilteredOrderItemTasks && (
              <Form.Field label="Task List" marginTop={canCreateTask && hasFilteredOrderItemTaskTypes ? 'medium' : 0}>
                {filteredOrderItemTasks.map(task => (
                  <Task key={task.id} orderItemTask={task} />
                ))}
              </Form.Field>
            )}
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => closeModal()}
          size="large"
        >
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageOrderItemTasksModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
